/**
 * Renumbering an invoice the server refused as a duplicate.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * `invoices` carries `@@unique([storeId, invoiceNumber])`, and the whole push
 * runs in ONE transaction. If the constraint did the rejecting, a single
 * duplicate would abort every table in that push, the client would retry, fail
 * identically, and the device would never drain again.
 *
 * So the server pre-checks and refuses only the offending row, reporting it as
 * `{ reason: 'DUPLICATE_INVOICE_NUMBER' }`. This is the other half: the sale
 * still has to reach the server, so its number has to change.
 *
 * ── Why it must run AFTER synchronize() ────────────────────────────────────
 * WatermelonDB marks everything it pushed as synced once the push resolves —
 * including the row the server threw away. Renumbering inside the push
 * callback would be undone by that bookkeeping, and the invoice would sit
 * locally marked clean, never re-sent, invisible. Exactly the silent loss this
 * whole effort is about.
 *
 * Updating the record AFTER the cycle marks it dirty again, so the next push
 * carries it with its new number.
 *
 * ── Why the client renumbers, and not the server ───────────────────────────
 * The sequence belongs to the device slot. The server has no slot context and
 * would have to guess, which is how two devices came to share a number in the
 * first place.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { Invoice } from '@/db/models/Billing';
import Store from '@/db/models/Store';
import { generateInvoiceNumber } from '@/billing/billingEngine';
import { localHighestSequence } from '@/billing/invoiceNumberRecovery';

export interface SyncRejection {
  table: string;
  id: string;
  reason: string;
  value: string;
}

/**
 * One invoice that changed number, for telling the operator.
 *
 * A renumbered invoice means the customer's printed copy no longer matches the
 * books, so this is never just logged.
 */
export interface RenumberOutcome {
  id: string;
  from: string;
  to: string;
  /** Which store's books this concerns — a till can switch between them. */
  storeId?: string;
}

/** Only duplicates are renumberable; anything else is reported, not rewritten. */
export function duplicateRejections(rejections: readonly SyncRejection[]): SyncRejection[] {
  return rejections.filter(
    (r) => r.table === 'invoices' && r.reason === 'DUPLICATE_INVOICE_NUMBER',
  );
}

/**
 * Give each refused invoice a fresh number from this device's own slot.
 *
 * Best-effort per row: one invoice that cannot be renumbered must not stop the
 * others. Anything left un-renumbered stays unsynced, which the sync-health
 * check then surfaces as a bill the server has not got — stuck and visible,
 * rather than stuck and silent.
 */
export async function renumberRejectedInvoices(
  rejections: readonly SyncRejection[],
  storeId: string | null,
  deviceSlot: number | null,
): Promise<RenumberOutcome[]> {
  const duplicates = duplicateRejections(rejections);
  if (duplicates.length === 0 || !storeId) return [];

  const outcomes: RenumberOutcome[] = [];

  try {
    const store = await database.get<Store>('stores').find(storeId);
    const prefix = store.invoicePrefix;

    // One sequence walk for the whole batch. Re-reading the highest per row
    // would hand the same number to two refused invoices.
    let next = (await localHighestSequence(storeId, deviceSlot ?? 0)) + 1;

    for (const rejection of duplicates) {
      try {
        const invoice = await database.get<Invoice>('invoices').find(rejection.id);
        const from = invoice.invoiceNumber;

        // Walk past anything this device can already see. The rejected number
        // is taken on the server; the candidates might be taken locally.
        let candidate = generateInvoiceNumber(next, prefix, deviceSlot);
        // Bounded: a pathological local state must not spin here forever.
        for (let guard = 0; guard < 1000; guard += 1) {
          const taken = await database
            .get<Invoice>('invoices')
            .query(Q.where('store_id', storeId), Q.where('invoice_number', candidate))
            .fetchCount();
          if (taken === 0) break;
          next += 1;
          candidate = generateInvoiceNumber(next, prefix, deviceSlot);
        }

        await database.write(async () => {
          await invoice.update((record) => {
            record.invoiceNumber = candidate;
            record.isSynced = false;
          });
        });

        outcomes.push({ id: rejection.id, from, to: candidate });
        next += 1;
      } catch (err) {
        console.warn(`[Sync] Could not renumber invoice ${rejection.id}`, err);
      }
    }
  } catch (err) {
    console.warn('[Sync] Could not renumber refused invoices', err);
  }

  return outcomes;
}

/** "RTC-00000007 is now RTC-00000012" — what the shopkeeper needs to hear. */
export function describeRenumbering(outcomes: readonly RenumberOutcome[]): string | null {
  if (outcomes.length === 0) return null;
  const first = outcomes
    .slice(0, 3)
    .map((o) => `${o.from} is now ${o.to}`)
    .join('; ');
  const rest = outcomes.length > 3 ? ` and ${outcomes.length - 3} more` : '';
  return (
    `${outcomes.length === 1 ? 'A bill was' : `${outcomes.length} bills were`} renumbered because ` +
    `another device had already used the number: ${first}${rest}. ` +
    `Reprint if the customer has a copy.`
  );
}

/**
 * Renumberings waiting to be acknowledged, kept across reloads.
 *
 * ── Why this is persisted and not just held in memory ──────────────────────
 * The first version kept these in a module variable. It worked until the
 * operator pressed refresh, at which point the notice vanished and the shop
 * was never told that a customer is holding a bill whose number no longer
 * matches the books. A notice a refresh destroys is not a notice.
 *
 * localStorage rather than the database: this is a per-device reminder for a
 * person, not a fact about the sale. The invoice itself is already correct and
 * already on the server — what survives here is only "somebody still needs to
 * reprint this". Losing it to a cleared browser costs a reprint, not data.
 *
 * Cleared when acknowledged, never on a timer: they have to go and do
 * something, and a message that expires by itself was never delivered.
 */
const STORAGE_KEY = 'bigxbook:sync.renumbered';

function readAll(): RenumberOutcome[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Shape-check each entry: a half-written or hand-edited value must degrade
    // to "nothing to report" rather than crashing the shell that renders it.
    return parsed.filter(
      (o): o is RenumberOutcome =>
        !!o && typeof o === 'object' &&
        typeof (o as RenumberOutcome).from === 'string' &&
        typeof (o as RenumberOutcome).to === 'string',
    );
  } catch {
    return [];
  }
}

function writeAll(outcomes: readonly RenumberOutcome[]): void {
  try {
    if (outcomes.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(outcomes));
  } catch {
    // A till that cannot write this still billed correctly and still synced.
  }
}

export function recordRenumbering(
  outcomes: readonly RenumberOutcome[],
  storeId?: string | null,
): void {
  if (outcomes.length === 0) return;
  const stamped = outcomes.map((o) => ({ ...o, storeId: o.storeId ?? storeId ?? undefined }));
  // De-duplicate by invoice id: re-running a cycle must not stack the same
  // notice twice in front of the operator.
  const merged = [...readAll().filter((e) => !stamped.some((n) => n.id === e.id)), ...stamped];
  writeAll(merged);
}

/** Outstanding notices for a store, or for everything when none is given. */
export function peekRenumbering(storeId?: string | null): RenumberOutcome[] {
  const all = readAll();
  if (!storeId) return all;
  // Entries written before stores were stamped are shown rather than hidden:
  // an unattributed reprint reminder is still a reprint reminder.
  return all.filter((o) => o.storeId == null || o.storeId === storeId);
}

/** The operator has seen it. Drops only what was shown to them. */
export function clearRenumbering(storeId?: string | null): void {
  if (!storeId) {
    writeAll([]);
    return;
  }
  writeAll(readAll().filter((o) => o.storeId != null && o.storeId !== storeId));
}
